import { DraftListing } from '@prisma/client';
import { ShopLoyaltyProgram } from '../loyalty';
import { ShopConfigResponse } from '../contentful/shopConfig';
import { Filter } from '../filters/filters';
import { FindItemMethod, ListingItemType } from '../sharetribe/listing';
import { User } from '../sharetribe/user';
import { ShopColors } from '../../util/colorPalettes';
import { MediaPayload } from '../../shopConfig/configHelper';

export enum StyleType {
  Default = 'DEFAULT',
  Condensed = 'CONDENSED',
}

export enum FontConfigSection {
  AboutPageGeneralInfoTitle = 'ABOUT_PAGE_GENERAL_INFO_TITLE',
  AboutPageFAQTitle = 'ABOUT_PAGE_FAQ_TITLE',
  FieldLabel = 'FIELD_LABEL',
}

export enum HowItWorksType {
  Default = 'DEFAULT',
  Swim = 'SWIM',
  Custom = 'CUSTOM',
}

// Note that if BuyerInfo/SellerInfo/TradeInInfo values change, custom "How It Works" icons
// will also need to be updated, as they are keyed on the media title.
export enum BuyerInfo {
  Browse = 'BROWSE',
  Buy = 'BUY',
  Verify = 'VERIFY',
}

export enum SellerInfo {
  PostIt = 'POST_IT',
  ShipIt = 'SHIP_IT',
  GetPaid = 'GET_PAID',
}

export enum TradeInInfoType {
  Submit = 'SUBMIT',
  Ship = 'SHIP',
  Earn = 'EARN',
}

export enum TradeInRestrictionType {
  MinimumNumberOfItems = 'MINIMUM_NUMBER_OF_ITEMS',
  MaximumNumberOfItems = 'MAXIMUM_NUMBER_OF_ITEMS',
}

export enum TradeInPriceRuleTarget {
  Item = 'ITEM',
  ItemWithCondition = 'ITEM_WITH_CONDITION',
  ItemWithCategory = 'ITEM_WITH_CATEGORY',
  Bundle = 'BUNDLE',
  ItemWithFindItemMethod = 'ITEM_WITH_FIND_ITEM_METHOD',
}

export enum FAQ {
  // Buyer
  Refund = 'REFUND',
  WhyShop = 'WHY_SHOP',
  ShippingLength = 'SHIPPING_LENGTH',
  WhatIfItemDoesntShip = 'WHAT_IF_ITEM_DOESNT_SHIP',
  Payment = 'PAYMENT',
  // Seller
  SellRequirements = 'SELL_REQUIREMENTS',
  WhySell = 'WHY_SELL',
  HowToSell = 'HOW_TO_SELL',
  HowToShip = 'HOW_TO_SHIP',
  SellingFees = 'SELLING_FEES',
  MisrepresentationClaim = 'MISREPRESENTATION_CLAIM',
  ListingReviewProcess = 'LISTING_REVIEW_PROCESS',
  SellerAdditional = 'SELLER_ADDITIONAL',
  SellerAdditional2 = 'SELLER_ADDITIONAL_2',
  StolenOrLost = 'STOLEN_OR_LOST',
  HowToGetPaid = 'HOW_TO_GET_PAID',

  // Trade-in
  WhyTradeIn = 'WHY_TRADE_IN',
  HowToTradeIn = 'HOW_TO_TRADE_IN',
  HowToShipTradeIn = 'HOW_TO_SHIP_TRADE_IN',
  TradeInGetPaid = 'TRADE_IN_GET_PAID',
  TradeInReviewProcess = 'TRADE_IN_REVIEW_PROCESS',
  TradeInRejected = 'TRADE_IN_REJECTED',
  TradeInCreditAmount = 'TRADE_IN_CREDIT_AMOUNT',
  TradeInPayoutDetails = 'TRADE_IN_PAYOUT_DETAILS',

  // Treet Protection
  BrandDirectReturnPolicy = 'BRAND_DIRECT_RETURN_POLICY',
  TreetProtection = 'TREET_PROTECTION',
  SellerProtection = 'SELLER_PROTECTION',
}

export enum TopbarLink {
  ShopAll = 'SHOP_ALL',
  Categories = 'CATEGORIES',
  SizeGuide = 'SIZE_GUIDE',
  MainSite = 'MAIN_SITE',
  OtherSite = 'OTHER_SITE',
}

export enum TopbarSection {
  Left = 'LEFT',
  Right = 'RIGHT',
}

export enum BrandDirectIconType {
  Default = 'DEFAULT',
  None = 'NONE',
  /**
   * @deprecated Use contentful brand direct icon config instead.
   */
  Dopple = 'DOPPLE',
  /**
   * @deprecated Use contentful brand direct icon config instead.
   */
  GoldieAndAce = 'GOLDIE_AND_ACE',
  /**
   * @deprecated Use contentful brand direct icon config instead.
   */
  ElizabethSuzann = 'ELIZABETH_SUZANN',
  /**
   * @deprecated Use contentful brand direct icon config instead.
   */
  DL1961 = 'DL1961',
  /**
   * @deprecated Use contentful brand direct icon config instead.
   */
  Sale25Off = 'SALE_25_OFF',
  /**
   * @deprecated Use contentful brand direct icon config instead.
   */
  Sale30Off = 'SALE_30_OFF',
  /**
   * @deprecated Use contentful brand direct icon config instead.
   */
  Sale50Off = 'SALE_50_OFF',
  /**
   * @deprecated Use contentful brand direct icon config instead.
   */
  Sale70Off = 'SALE_70_OFF',
}

export enum AboutPageInfoTabSection {
  About = 'ABOUT',
  HowItWorks = 'HOW_IT_WORKS',
  // TODO (TREET-3995) Update enum string in Contentful config.
  TradeInHowItWorks = 'BUYBACK_HOW_IT_WORKS',
}

export enum ListingFlowSection {
  VariantOptions = 'VARIANT_OPTIONS',
  Condition = 'CONDITION',
  Quirks = 'QUIRKS',
  Description = 'DESCRIPTION',
  Photos = 'PHOTOS',
  Price = 'PRICE',
  TradeInPrice = 'TRADE_IN_PRICE',
  AcknowledgementCheckbox = 'ACKNOWLEDGEMENT_CHECKBOX',
  InternalBrandNotes = 'INTERNAL_BRAND_NOTES',
  StockPhotos = 'STOCK_PHOTOS',
}

export enum ProgressBarSection {
  FindItem = 'FIND_ITEM',
  ItemDetails = 'ITEM_DETAILS',
  Condition = 'CONDITION',
  Photos = 'PHOTOS',
  Price = 'PRICE',
  Acknowledgement = 'ACKNOWLEDGEMENT',
}

export type ConditionOption = {
  key: string;
  label: string;
  description?: string;
  includedTypes?: ListingItemType[];
};

export enum FulfillmentMethod {
  Treet = 'TREET',
  Shopify = 'SHOPIFY',
  RetailReworks = 'RETAIL_REWORKS',
  Peer = 'PEER',
}

export enum HeroAction {
  Shop = 'SHOP',
  Sell = 'SELL',
  ExternalLink = 'EXTERNAL_LINK',
}

export enum FooterLogo {
  Brand = 'LEFT',
  Treet = 'RIGHT',
  SuperCircle = 'SUPER_CIRCLE',
}

export enum CustomerExperience {
  Buy = 'BUY',
  Sell = 'SELL',
  TradeIn = 'TRADE_IN',
}

export enum Tier {
  Basic = 'BASIC',
  Premium = 'PREMIUM',
}

export type BuyerSellerInfo = {
  [key in BuyerInfo | SellerInfo | TradeInInfoType]: { title: string; description: string };
};

export type TradeInInfo = {
  [key in TradeInInfoType]: { title: string; description: string };
};

export type FAQObject = {
  title: string;
  description: string;
};

export type TopbarLayoutItem = {
  type: TopbarLink;
  config?: { label?: string; url?: string };
  section: TopbarSection;
};

export type HeroActionItem = {
  type: HeroAction;
  config?: { label?: string; url?: string };
};

export type PricingRestriction = {
  type: string;
  tag?: string;
  minimumPrice?: number;
  minimumPercentage?: number;
};

// A function that takes bundle & credit recipient and outputs a string.
// Minimally typed - only types required for function implementation
// are included. Add more as necessary.
type CustomCreditDescriptionFn = (data: {
  bundle: { shipFromAddress: { state: string }; displayId: string };
  recipient: User;
}) => string;

type AdditionalPayoutSettingsType = {
  additionalShopifyDiscountCodeRequirements?: {
    mutation?: Record<string, any>;
  };
  additionalShopifyGiftCardRequirements?: {
    giftCardExpiresAfterDays?: number;
  };
  creditCodeHelperText?: string;
  tradeInCreditPayoutBundleAmount?: number;

  buildCustomCreditDescription?: CustomCreditDescriptionFn;
};

export enum AboutTreetPages {
  AboutTreet = 'ABOUT_TREET',
  AboutFAQ = 'ABOUT_FAQ',
  TreetProtection = 'TREET_PROTECTION',
}

export type PrivateShopConfig = {
  filters: Filter[];
  footerLogos: FooterLogo[];
  additionalPayoutSettings?: AdditionalPayoutSettingsType;
  tradeInAllowedFindItemMethods?: FindItemMethod[];
  hideHeaderLogoAtPathnames?: string[];
  hideConfettiAtCheckout?: boolean;
  loyaltyPointsConfig?: Partial<ShopLoyaltyProgram>;
  // Used when there are multiple variant options for the size field
  allSizeVariantOptionNames?: string[];
  // Sorts and filters product media for the ingest product data job
  // If you want this change to take place everywhere, as of 09/24/25, you have to still add a CHOOSE_STOCK_PHOTO_OPTION
  getMediaForProduct?: (media?: MediaPayload[]) => MediaPayload[];
  // Selects images from product media to be included in the variant media array
  getMediaForVariant?: (
    media?: MediaPayload[],
    selectedVariantOptions?: Record<string, string | null>
  ) => MediaPayload[];
  requiresBrandApprovalFn?: (listing: DraftListing) => boolean;
  shouldDisableRemovingFeaturedStockPhoto?: boolean;
};

export type DefaultShopStylesConfig = {
  defaultShopStyles: ShopColors;
};

export type ShopConfigV2 = ShopConfigResponse & PrivateShopConfig & DefaultShopStylesConfig;
